import React, { useState } from 'react';
import { Card, Select, Input, Row, Col, Space, Button, message, Modal, Switch } from 'antd';
import { FileTextOutlined, SearchOutlined, PlusOutlined, SendOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { DataTable, MoneyCell, PageLayout } from '../../ui';
import dayjs from 'dayjs';
import {
  useGetInvoicesListQuery,
  useSendInvoiceToClientMutation,
  useCancelInvoiceMutation,
  InvoiceListItem
} from '../../store/api/financeApi';
import { CreateClientInvoiceModal, SendInvoiceModal, EditClientInvoiceModal, CreateInvoiceAdjustModal } from '../../components/finance';
import { financeDirectionLabel } from '../../utils/enumUiLabels';

const { Option } = Select;

const FinanceInvoicesPage: React.FC = () => {
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [direction, setDirection] = useState<string | undefined>(undefined);
  const [counterpartyType, setCounterpartyType] = useState<string | undefined>(undefined);
  const [search, setSearch] = useState<string>('');
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [invoiceIdToSend, setInvoiceIdToSend] = useState<number | null>(null);
  const [editingInvoice, setEditingInvoice] = useState<InvoiceListItem | null>(null);
  const [adjustSourceInvoice, setAdjustSourceInvoice] = useState<InvoiceListItem | null>(null);
  const [sendLoading, setSendLoading] = useState(false);
  const [showCancelled, setShowCancelled] = useState(false);

  const { data, isLoading, refetch } = useGetInvoicesListQuery({
    page,
    limit,
    direction: direction || undefined,
    counterpartyType: counterpartyType || undefined,
    search: search.trim() || undefined,
    includeCancelled: showCancelled || undefined
  });

  const [sendInvoiceToClient] = useSendInvoiceToClientMutation();
  const [cancelInvoice, { isLoading: cancelLoading }] = useCancelInvoiceMutation();

  const handleConfirmSend = async (sentToClientAtIso: string) => {
    if (invoiceIdToSend == null) return;
    setSendLoading(true);
    try {
      const result = await sendInvoiceToClient({
        invoiceId: invoiceIdToSend,
        sentToClientAt: sentToClientAtIso
      }).unwrap();
      if (result.idempotent) {
        message.info('Счёт уже был отправлен ранее');
      } else {
        message.success('Счёт отправлен клиенту');
      }
      setSendModalOpen(false);
      setInvoiceIdToSend(null);
      await refetch();
    } catch (e: any) {
      message.error(e?.data?.error || 'Не удалось отправить счёт');
    } finally {
      setSendLoading(false);
    }
  };

  const requestCancelInvoice = (row: InvoiceListItem) => {
    Modal.confirm({
      title: 'Удалить счёт?',
      content: 'Счёт будет помечен как отменённый (CANCELLED) и скроется из списка.',
      okText: 'Удалить',
      okType: 'danger',
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          await cancelInvoice(row.id).unwrap();
          message.success('Счёт отменён');
          await refetch();
        } catch (e: any) {
          message.error(e?.data?.error || 'Не удалось удалить счёт');
        }
      }
    });
  };

  const invoices = data?.invoices ?? [];
  const pagination = data?.pagination ?? { page: 1, limit: 20, total: 0 };

  const columns: ColumnsType<InvoiceListItem> = [
    {
      title: 'Номер счета',
      dataIndex: 'number',
      key: 'number',
      width: 180,
      className: 'primary-cell',
      render: (val: string, r: InvoiceListItem) => (
        <div>
          <div>{val || '—'}</div>
          {r.isAdjustment && r.parentInvoice != null && (
            <div className="primary-cell-meta">
              Корректировка к счету #{r.parentInvoice.id}
              {r.parentInvoice.number ? ` (${r.parentInvoice.number})` : ''}
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Дата',
      dataIndex: 'invoiceDate',
      key: 'invoiceDate',
      width: 110,
      render: (d: string) => (d ? dayjs(d).format('DD.MM.YYYY') : '—')
    },
    {
      title: 'Тип счета',
      key: 'direction',
      width: 120,
      render: (_, r) => financeDirectionLabel(r.direction)
    },
    {
      title: 'Контрагент',
      key: 'counterparty',
      ellipsis: true,
      render: (_, r) => {
        if (r.counterpartyType === 'CLIENT' && r.application?.client) {
          return r.application.client.name ?? '—';
        }
        if (r.counterpartyType === 'CARRIER' && r.route?.carrier) {
          return r.route.carrier.name ?? '—';
        }
        return '—';
      }
    },
    {
      title: 'Заявка',
      key: 'application',
      width: 130,
      render: (_, r) => (r.application?.internalNumber ? `#${r.application.internalNumber}` : '—')
    },
    {
      title: 'Маршрут',
      key: 'route',
      width: 90,
      render: (_, r) => (r.route ? `#${r.route.id}` : '—')
    },
    {
      title: 'Сумма',
      dataIndex: 'amountWithVAT',
      key: 'amountWithVAT',
      width: 120,
      align: 'right',
      render: (v: number) => <MoneyCell value={Number(v ?? 0)} type="income" />
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 150,
      render: (s: string) => {
        if (s === 'CREATED') return 'Создан';
        if (s === 'SENT') return 'Отправлен';
        if (s === 'CANCELLED') return 'Отменён';
        return s || '—';
      }
    },
    {
      title: 'Отправлен клиенту',
      dataIndex: 'sentToClientAt',
      key: 'sentToClientAt',
      width: 130,
      render: (d: string | null, row: InvoiceListItem) =>
        row.status === 'SENT' && d ? dayjs(d).format('DD.MM.YYYY HH:mm') : '—'
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 300,
      fixed: 'right',
      render: (_, r) => {
        if (String(r.status || '') === 'CREATED') {
          return (
            <Space size="small" wrap>
              {r.direction === 'INCOME' && r.counterpartyType === 'CLIENT' && (
                <Button
                  type="primary"
                  size="small"
                  icon={<SendOutlined />}
                  onClick={() => {
                    setInvoiceIdToSend(r.id);
                    setSendModalOpen(true);
                  }}
                >
                  Отправить
                </Button>
              )}
              <Button size="small" icon={<EditOutlined />} onClick={() => setEditingInvoice(r)}>
                Редактировать
              </Button>
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                loading={cancelLoading}
                onClick={() => requestCancelInvoice(r)}
              >
                Удалить
              </Button>
            </Space>
          );
        }
        if (
          String(r.status || '') === 'SENT' &&
          r.direction === 'INCOME' &&
          r.counterpartyType === 'CLIENT' &&
          !r.isAdjustment
        ) {
          return (
            <Button size="small" type="default" onClick={() => setAdjustSourceInvoice(r)}>
              Создать корректировку
            </Button>
          );
        }
        return null;
      }
    }
  ];

  return (
    <PageLayout
      title={
        <Space>
          <FileTextOutlined />
          <span>Счета</span>
        </Space>
      }
      subtitle="Клиентские и перевозческие счета"
      actions={
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModalVisible(true)}>
          Создать счёт
        </Button>
      }
    >
      <Card>
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col>
            <Input
              placeholder="Поиск по номеру счета"
              prefix={<SearchOutlined />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              allowClear
              style={{ width: 220 }}
            />
          </Col>
          <Col>
            <Select
              placeholder="Тип счета"
              allowClear
              value={direction || undefined}
              onChange={setDirection}
              style={{ width: 140 }}
            >
              <Option value="INCOME">{financeDirectionLabel('INCOME')}</Option>
              <Option value="EXPENSE">{financeDirectionLabel('EXPENSE')}</Option>
            </Select>
          </Col>
          <Col>
            <Select
              placeholder="Контрагент"
              allowClear
              value={counterpartyType || undefined}
              onChange={setCounterpartyType}
              style={{ width: 140 }}
            >
              <Option value="CLIENT">Клиент</Option>
              <Option value="CARRIER">Перевозчик</Option>
            </Select>
          </Col>
          <Col>
            <Space>
              <span>Показать отменённые</span>
              <Switch checked={showCancelled} onChange={(v) => { setShowCancelled(v); setPage(1); }} />
            </Space>
          </Col>
        </Row>

        <DataTable<InvoiceListItem>
          rowKey="id"
          loading={isLoading}
          columns={columns}
          dataSource={invoices}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            showSizeChanger: false,
            showTotal: (t) => `Всего: ${t}`,
            onChange: (p) => setPage(p)
          }}
          scroll={{ x: 'max-content' }}
        />

        <CreateClientInvoiceModal
          visible={createModalVisible}
          onCancel={() => setCreateModalVisible(false)}
          onSuccess={() => {
            setCreateModalVisible(false);
            void refetch();
          }}
        />

        <SendInvoiceModal
          open={sendModalOpen}
          loading={sendLoading}
          onCancel={() => {
            setSendModalOpen(false);
            setInvoiceIdToSend(null);
          }}
          onSend={(iso) => void handleConfirmSend(iso)}
        />

        <EditClientInvoiceModal
          open={!!editingInvoice}
          invoice={editingInvoice}
          onCancel={() => setEditingInvoice(null)}
          onSuccess={() => {
            setEditingInvoice(null);
            void refetch();
          }}
        />

        <CreateInvoiceAdjustModal
          open={!!adjustSourceInvoice}
          sourceInvoice={adjustSourceInvoice}
          onCancel={() => setAdjustSourceInvoice(null)}
          onSuccess={() => {
            setAdjustSourceInvoice(null);
            void refetch();
          }}
        />
      </Card>
    </PageLayout>
  );
};

export default FinanceInvoicesPage;
